import { PageContent } from '@/components/shared/page-content'
import { HeaderSection } from '@/components/shared/header-section'
import { Card } from '@/components/ui/card'
import { gql, useMutation, useQuery } from '@apollo/client'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { useState, useEffect } from 'react'
import { toast } from 'sonner'

const GET_SITE_SETTINGS = gql`
  query GET_SITE_SETTINGS {
    findFirstShopSetting {
      id
      cart_pdf_enabled
      cart_pdf_email
      cart_pdf_email_template
      magic_link_email
      magic_link_email_template
      order_confirmation_email_enabled
      order_confirmation_email_template
      order_confirmation_email_to_client
      order_confirmation_email_to_creator
      invitation_email
      invitation_email_template
      partner_request_email
    }
  }
`

const UPDATE_ALL_EMAIL_SETTINGS = gql`
  mutation UPDATE_ALL_EMAIL_SETTINGS(
    $cart_pdf_enabled: Boolean!
    $cart_pdf_email: String!
    $cart_pdf_email_template: String!
    $magic_link_email: String!
    $magic_link_email_template: String!
    $order_confirmation_email_enabled: Boolean!
    $order_confirmation_email_template: String!
    $order_confirmation_email_to_client: Boolean!
    $order_confirmation_email_to_creator: Boolean!
    $invitation_email: String!
    $invitation_email_template: String!
    $partner_request_email: String!
  ) {
    updateEmailSettings(
      cart_pdf_enabled: $cart_pdf_enabled
      cart_pdf_email: $cart_pdf_email
      cart_pdf_email_template: $cart_pdf_email_template
      magic_link_email: $magic_link_email
      magic_link_email_template: $magic_link_email_template
      order_confirmation_email_enabled: $order_confirmation_email_enabled
      order_confirmation_email_template: $order_confirmation_email_template
      order_confirmation_email_to_client: $order_confirmation_email_to_client
      order_confirmation_email_to_creator: $order_confirmation_email_to_creator
      invitation_email: $invitation_email
      invitation_email_template: $invitation_email_template
      partner_request_email: $partner_request_email
    ) {
      cart_pdf_enabled
      cart_pdf_email
      cart_pdf_email_template
      magic_link_email
      magic_link_email_template
      order_confirmation_email_enabled
      order_confirmation_email_template
      order_confirmation_email_to_client
      order_confirmation_email_to_creator
      invitation_email
      invitation_email_template
      partner_request_email
    }
  }
`

export default function Emails() {
  const { data, loading } = useQuery(GET_SITE_SETTINGS)
  const [updateAllEmailSettings, { loading: isSaving }] = useMutation(
    UPDATE_ALL_EMAIL_SETTINGS
  )

  const [formState, setFormState] = useState({
    enabled: false,
    email: '',
    template: '',
  })

  const [magicLinkState, setMagicLinkState] = useState({
    email: '',
    template: '',
  })

  const [orderConfirmationState, setOrderConfirmationState] = useState({
    enabled: false,
    template: '',
    toClient: false,
    toCreator: false,
  })

  const [invitationState, setInvitationState] = useState({
    email: '',
    template: '',
  })

  const [partnerRequestState, setPartnerRequestState] = useState({
    email: '',
  })

  // Initialize form state when data is loaded
  const initializeFormState = () => {
    if (data?.findFirstShopSetting) {
      setFormState({
        enabled: data.findFirstShopSetting.cart_pdf_enabled || false,
        email: data.findFirstShopSetting.cart_pdf_email || '',
        template: data.findFirstShopSetting.cart_pdf_email_template || '',
      })

      setMagicLinkState({
        email: data.findFirstShopSetting.magic_link_email || '',
        template: data.findFirstShopSetting.magic_link_email_template || '',
      })

      setOrderConfirmationState({
        enabled:
          data.findFirstShopSetting.order_confirmation_email_enabled || false,
        template:
          data.findFirstShopSetting.order_confirmation_email_template || '',
        toClient:
          data.findFirstShopSetting.order_confirmation_email_to_client || false,
        toCreator:
          data.findFirstShopSetting.order_confirmation_email_to_creator ||
          false,
      })

      setInvitationState({
        email: data.findFirstShopSetting.invitation_email || '',
        template: data.findFirstShopSetting.invitation_email_template || '',
      })

      setPartnerRequestState({
        email: data.findFirstShopSetting.partner_request_email || '',
      })
    }
  }

  useEffect(() => {
    initializeFormState()
  }, [data])

  const handleSubmitAllSettings = async () => {
    try {
      await updateAllEmailSettings({
        variables: {
          // PDF Cart settings
          cart_pdf_enabled: formState.enabled,
          cart_pdf_email: formState.email,
          cart_pdf_email_template: formState.template,
          // Magic Link settings
          magic_link_email: magicLinkState.email,
          magic_link_email_template: magicLinkState.template,
          // Order Confirmation settings
          order_confirmation_email_enabled: orderConfirmationState.enabled,
          order_confirmation_email_template: orderConfirmationState.template,
          order_confirmation_email_to_client: orderConfirmationState.toClient,
          order_confirmation_email_to_creator: orderConfirmationState.toCreator,
          // Invitation Email settings
          invitation_email: invitationState.email,
          invitation_email_template: invitationState.template,
          // Partner Request Email settings
          partner_request_email: partnerRequestState.email,
        },
      })
      toast.success('Email settings updated successfully')
    } catch (error) {
      console.error('Failed to update email settings:', error)
      toast.error('Failed to update email settings')
    }
  }

  const handleCancel = () => {
    initializeFormState()
  }

  if (loading) {
    return <div>Loading...</div>
  }

  return (
    <PageContent>
      <h1 className="text-2xl font-bold mb-8">Email Settings</h1>

      <div className="max-w-[1000px]">
        {/* Cart to PDF Settings */}
        <div className="mb-8">
          <div className="grid grid-cols-12 gap-x-8">
            <div className="col-span-4">
              <h2 className="text-lg font-semibold">Save Cart to PDF</h2>
              <p className="text-sm text-gray-600 mt-1">
                Configure settings for the cart to PDF email feature.
              </p>
            </div>

            <div className="col-span-8">
              <div className="mb-6">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formState.enabled}
                    onCheckedChange={checked =>
                      setFormState(prev => ({ ...prev, enabled: checked }))
                    }
                  />
                  <label className="text-sm font-medium">
                    Enable Cart to PDF Feature
                  </label>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">
                  From Email
                </label>
                <Input
                  value={formState.email}
                  onChange={e =>
                    setFormState(prev => ({ ...prev, email: e.target.value }))
                  }
                  placeholder="Enter notification email"
                  type="email"
                  className="w-full"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">
                  Email Template Id
                </label>
                <Input
                  value={formState.template}
                  onChange={e =>
                    setFormState(prev => ({
                      ...prev,
                      template: e.target.value,
                    }))
                  }
                  placeholder="Enter email template"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border-t my-8"></div>

        {/* Magic Link Email Settings */}
        <div className="mb-8">
          <div className="grid grid-cols-12 gap-x-8">
            <div className="col-span-4">
              <h2 className="text-lg font-semibold">Magic Link Email</h2>
              <p className="text-sm text-gray-600 mt-1">
                Configure settings for magic link email authentication.
              </p>
            </div>

            <div className="col-span-8">
              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">
                  From Email
                </label>
                <Input
                  value={magicLinkState.email}
                  onChange={e =>
                    setMagicLinkState(prev => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  placeholder="Enter notification email"
                  type="email"
                  className="w-full"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">
                  Email Template Id
                </label>
                <Input
                  value={magicLinkState.template}
                  onChange={e =>
                    setMagicLinkState(prev => ({
                      ...prev,
                      template: e.target.value,
                    }))
                  }
                  placeholder="Enter email template"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border-t my-8"></div>

        {/* Order Confirmation Settings */}
        <div className="mb-8">
          <div className="grid grid-cols-12 gap-x-8">
            <div className="col-span-4">
              <h2 className="text-lg font-semibold">
                Order Confirmation Email
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Configure settings for order confirmation emails.
                <br />
                <br />
                Under development
              </p>
            </div>

            <div className="col-span-8">
              <div className="mb-6">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={orderConfirmationState.enabled}
                    onCheckedChange={checked =>
                      setOrderConfirmationState(prev => ({
                        ...prev,
                        enabled: checked,
                      }))
                    }
                    disabled={true}
                  />
                  <label className="text-sm font-medium">
                    Enable Order Confirmation Email
                  </label>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">
                  Email Template Id
                </label>
                <Input
                  value={orderConfirmationState.template}
                  onChange={e =>
                    setOrderConfirmationState(prev => ({
                      ...prev,
                      template: e.target.value,
                    }))
                  }
                  placeholder="Enter email template"
                  className="w-full"
                  disabled={true}
                />
              </div>

              <div className="mb-6">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={orderConfirmationState.toClient}
                    onCheckedChange={checked =>
                      setOrderConfirmationState(prev => ({
                        ...prev,
                        toClient: checked,
                      }))
                    }
                    disabled={true}
                  />
                  <label className="text-sm font-medium">
                    Send confirmation to client
                  </label>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-center space-x-2 opacity-60">
                  <Switch
                    checked={orderConfirmationState.toCreator}
                    onCheckedChange={checked =>
                      setOrderConfirmationState(prev => ({
                        ...prev,
                        toCreator: checked,
                      }))
                    }
                    disabled={true}
                  />
                  <label className="text-sm font-medium">
                    Send confirmation to creator (if different from client)
                  </label>
                  <div className="text-xs text-gray-500 ml-2">
                    Under development - Used when the order is created by an
                    account manager or agent
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t my-8"></div>

        {/* Customer Invitation Email Settings */}
        <div className="mb-8">
          <div className="grid grid-cols-12 gap-x-8">
            <div className="col-span-4">
              <h2 className="text-lg font-semibold">
                Customer Invitation Email
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Configure settings for customer invitation emails.
              </p>
            </div>

            <div className="col-span-8">
              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">
                  From Email
                </label>
                <Input
                  value={invitationState.email}
                  onChange={e =>
                    setInvitationState(prev => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  placeholder="Enter notification email"
                  type="email"
                  className="w-full"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">
                  Email Template Id
                </label>
                <Input
                  value={invitationState.template}
                  onChange={e =>
                    setInvitationState(prev => ({
                      ...prev,
                      template: e.target.value,
                    }))
                  }
                  placeholder="Enter email template"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border-t my-8"></div>

        {/* Partner Request Email Settings */}
        <div className="mb-8">
          <div className="grid grid-cols-12 gap-x-8">
            <div className="col-span-4">
              <h2 className="text-lg font-semibold">Partner Request Email</h2>
              <p className="text-sm text-gray-600 mt-1">
                Configure the email address that receives partner request form
                submissions from the B2B portal.
              </p>
              <p className="text-sm text-gray-500 mt-2 italic">
                Note: The sender (from) email will be the same as Magic Link
                From Email.
              </p>
            </div>

            <div className="col-span-8">
              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">
                  Recipient Email
                </label>
                <Input
                  value={partnerRequestState.email}
                  onChange={e =>
                    setPartnerRequestState(prev => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  placeholder="Enter recipient email (e.g., sales@company.com)"
                  type="email"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1000px] border-t mt-8 pt-6 flex justify-end items-center gap-4">
        <Button variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmitAllSettings} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </PageContent>
  )
}
